import React, { useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import { NhisStatus } from "types/nhis";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";

// --- 상태 → 단계/메시지 매핑 ---

interface StepInfo {
  step: number;
  message: string;
  icon: string;
  color: string;
}

const STATUS_STEP_MAP: Record<NhisStatus, StepInfo> = {
  PENDING: {
    step: 1,
    message: "현재 환자가 동의 및 인증수단을 선택하고 있습니다 (1단계)",
    icon: "Clock",
    color: "Blue",
  },
  AUTH_REQUESTED: {
    step: 2,
    message: "현재 환자가 본인인증을 진행하고 있습니다 (2단계)",
    icon: "ID Card",
    color: "Blue",
  },
  FETCHED: {
    step: 3,
    message: "현재 환자가 처방전을 선택하고 있습니다 (3단계)",
    icon: "Document Check",
    color: "Blue",
  },
  DELIVERED: {
    step: 3,
    message: "처방전이 등록되었습니다",
    icon: "Check Circle",
    color: "Green",
  },
  FAILED: {
    step: 0,
    message:
      "인증/조회에 실패했습니다. 환자에게 기존 링크로 재시도를 안내해주세요",
    icon: "Exclamation Mark Circle",
    color: "Red",
  },
  EXPIRED: {
    step: 0,
    message: "요청이 만료되었습니다 (24시간)",
    icon: "Clock X",
    color: "Gray",
  },
  CANCELLED: {
    step: 0,
    message: "약사가 요청을 취소했습니다",
    icon: "X",
    color: "Gray",
  },
};

// --- 유틸 ---

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;

  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}일 전`;
}

// --- Props ---

export interface NhisProgressDashboardProps {
  status: NhisStatus;
  patientName: string;
  createdAt: string;
  consentUrl?: string;
  errorMessage?: string;
  onClose?: () => void;
  children?: React.ReactNode; // NhisStepPreview 삽입 슬롯
  showStatusHeader?: boolean; // false이면 환자 정보/상태 카드 숨김
}

// --- 스타일 ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 4px 0;
  width: 100%;
`;

const AccordionBox = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 10px;
  overflow: hidden;
`;

const AccordionContent = styled.div`
  padding: 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PatientMeta = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  line-height: 1.4;
`;

const PatientName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const MetaDot = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const MetaText = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const StatusMessage = styled.span<{ $colorKey: string }>`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  color: ${({ $colorKey }) =>
    COLOR_SYSTEM.get($colorKey as any)?.[700] ?? "#333"};
`;

const StatusCard = styled.div<{ $colorKey: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 10px;
  background: ${({ $colorKey }) =>
    COLOR_SYSTEM.get($colorKey as any)?.[50] ?? "#f5f5f5"};
  border: 1px solid
    ${({ $colorKey }) =>
      COLOR_SYSTEM.get($colorKey as any)?.[200] ?? "#e5e5e5"};
`;

const StatusIcon = styled.div<{ $colorKey: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $colorKey }) =>
    COLOR_SYSTEM.get($colorKey as any)?.[100] ?? "#e5e5e5"};
  flex-shrink: 0;
`;

const LinkSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LinkText = styled.span`
  flex: 1;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  background: none;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

// --- 컴포넌트 ---

const NhisProgressDashboard: React.FC<NhisProgressDashboardProps> = ({
  status,
  patientName,
  createdAt,
  consentUrl,
  errorMessage,
  onClose,
  children,
  showStatusHeader = true,
}) => {
  const info = STATUS_STEP_MAP[status];
  const [showGuide, setShowGuide] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const isInProgress = status === "PENDING" || status === "AUTH_REQUESTED" || status === "FETCHED";

  const handleCopyUrl = async () => {
    if (!consentUrl) return;
    try {
      await navigator.clipboard.writeText(consentUrl);
      ToastManager.show({ content: "링크가 복사되었습니다." });
    } catch {
      ToastManager.show({ content: "링크 복사에 실패했습니다." });
    }
  };

  return (
    <Container>
      {/* 환자 정보 + 현재 상태 */}
      {showStatusHeader && (
        <HeaderBlock>
          {/* 환자 이름 + 요청 시간 메타 */}
          <PatientMeta>
            <PatientName>{patientName} 환자</PatientName>
            <MetaDot>·</MetaDot>
            <MetaText>요청 {formatRelativeTime(createdAt)}</MetaText>
          </PatientMeta>

          {/* 상태 카드 — 진행 중: 고정 문구, 종결 상태: info.message */}
          {isInProgress ? (
            <StatusCard $colorKey={info.color} style={{ padding: "20px" }}>
              <StatusIcon $colorKey={info.color}>
                <Icon
                  name={info.icon}
                  size={18}
                  color={COLOR_SYSTEM.get(info.color as any)?.[500] ?? "#666"}
                />
              </StatusIcon>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  lineHeight: 1.4,
                  color: COLOR_SYSTEM.get(info.color as any)?.[700] ?? "#333",
                }}
              >
                처방전이 등록되면 알림톡으로 알려드릴게요
              </div>
            </StatusCard>
          ) : (
            <StatusCard $colorKey={info.color}>
              <StatusIcon $colorKey={info.color}>
                <Icon
                  name={info.icon}
                  size={18}
                  color={COLOR_SYSTEM.get(info.color as any)?.[500] ?? "#666"}
                />
              </StatusIcon>
              <StatusMessage $colorKey={info.color} style={{ fontWeight: 700 }}>
                {status === "FAILED" && errorMessage
                  ? errorMessage
                  : info.message}
              </StatusMessage>
            </StatusCard>
          )}
        </HeaderBlock>
      )}

      {/* 아코디언 영역 (가이드 + 링크) — 상단 정보와 시각적 분리 */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px" }}>
      {/* 환자 화면 가이드 토글 (진행 중 상태에서만) */}
      {children && isInProgress && (
        <AccordionBox>
          <ToggleButton onClick={() => setShowGuide(!showGuide)}>
            <span>환자가 거치는 화면 가이드 보기</span>
            <Icon
              name={showGuide ? "Chevron Up" : "Chevron Down"}
              size={16}
              color={COLOR_SYSTEM.get("Gray")?.[500]}
            />
          </ToggleButton>
          {showGuide && <AccordionContent>{children}</AccordionContent>}
        </AccordionBox>
      )}

      {/* 발송 링크 토글 */}
      {consentUrl && (
        <AccordionBox>
          <ToggleButton onClick={() => setShowLink(!showLink)}>
            <span>환자에게 발송된 링크</span>
            <Icon
              name={showLink ? "Chevron Up" : "Chevron Down"}
              size={16}
              color={COLOR_SYSTEM.get("Gray")?.[500]}
            />
          </ToggleButton>
          {showLink && (
            <AccordionContent>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: COLOR_SYSTEM.get("Gray")?.[500],
                  lineHeight: 1.5,
                }}
              >
                환자가 링크를 받지 못했거나 찾지 못하는 경우 아래 링크를 복사해 문자로 다시 보내주세요.
              </div>
              <LinkSection>
                <LinkText>{consentUrl}</LinkText>
              </LinkSection>
              <button
                type="button"
                onClick={handleCopyUrl}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: 1.5,
                  color: COLOR_SYSTEM.get("Blue")?.[500],
                  textDecoration: "underline",
                  alignSelf: "flex-start",
                  display: "block",
                }}
              >
                링크 복사하기
              </button>
            </AccordionContent>
          )}
        </AccordionBox>
      )}
      </div>

      {/* 닫기 */}
      {onClose && (
        <ButtonRow>
          <Button
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={onClose}
          >
            확인
          </Button>
        </ButtonRow>
      )}
    </Container>
  );
};

export default NhisProgressDashboard;
